import React, { useState } from 'react';
import { Container, Card, Button, Modal } from 'react-bootstrap';
import { FaUserMinus } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_ENDPOINTS } from '../config/api';
import './DeleteAccount.css';
import BackToHome from '../components/BackToHome';

const DeleteAccount = () => {
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteClick = () => setShowConfirm(true);
  const handleConfirmClose = () => !deleting && setShowConfirm(false);

  const handleConfirmDelete = async () => {
    if (!currentUser) return;
    setDeleting(true);
    try {
      const token = await currentUser.getIdToken();
      const res = await fetch(API_ENDPOINTS.AUTH.DELETE_ACCOUNT, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Request failed (${res.status})`);
      }
      await logout();
      toast.success('Your account and all data have been permanently deleted.');
      navigate('/', { replace: true });
    } catch (err) {
      const message = err.message || 'Failed to delete account. Please try again.';
      const isCorsOrNetwork = message.includes('Failed to fetch') || message.includes('NetworkError') || message.includes('CORS');
      toast.error(isCorsOrNetwork ? 'Request blocked or network error. Try again or check your connection.' : message);
      setDeleting(false);
    }
  };

  return (
    <Container className="py-5">
      <BackToHome />
      <Card>
        <Card.Header className="delete-account-header">
          <h2 className="mb-0">
            <FaUserMinus className="me-2" />
            Delete Your FarmX Account
          </h2>
        </Card.Header>
        <Card.Body className="delete-account-body">
          <div className="delete-account-intro">
            <p>
              FarmX allows you to permanently delete your account and all associated data.
            </p>
          </div>

          {currentUser && (
            <div className="delete-account-section delete-account-now-box">
              <h4 className="section-title">Delete your account from this website</h4>
              <p className="subtitle">You are signed in as <strong>{currentUser.email}</strong>. You can delete your account and all data from here.</p>
              <ul className="bullet-list">
                <li>Your account and profile will be permanently deleted</li>
                <li>All your ads and their images will be removed</li>
                <li>Your chat history will be deleted</li>
                <li>This action cannot be undone</li>
              </ul>
              <Button
                variant="danger"
                size="lg"
                className="delete-account-now-btn"
                onClick={handleDeleteClick}
                disabled={deleting}
              >
                <FaUserMinus className="me-2" />
                Permanently delete my account
              </Button>
            </div>
          )}
        </Card.Body>
      </Card>

      <Modal show={showConfirm} onHide={handleConfirmClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete account permanently?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This will permanently delete your account and all your data (ads, images, chats). This cannot be undone.</p>
          <p className="mb-0">Are you sure you want to continue?</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleConfirmClose} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Yes, delete my account'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DeleteAccount;
